// Activity type enum - matches backend ActivityType.java
export enum ActivityType {
  // Candidate lifecycle
  CANDIDATE_APPLIED = 'CANDIDATE_APPLIED',
  CANDIDATE_MOVED = 'CANDIDATE_MOVED',
  COMMENT_ADDED = 'COMMENT_ADDED',
  DESCRIPTION_UPDATED = 'DESCRIPTION_UPDATED',
  CANDIDATE_UPDATED = 'CANDIDATE_UPDATED',

  // Interview management
  INTERVIEW_BOOKED = 'INTERVIEW_BOOKED',
  INTERVIEW_CONFIRMED = 'INTERVIEW_CONFIRMED',
  INTERVIEW_CANCELLED = 'INTERVIEW_CANCELLED',
  INTERVIEW_COMPLETED = 'INTERVIEW_COMPLETED',
  INTERVIEW_NO_SHOW = 'INTERVIEW_NO_SHOW',
  INTERVIEW_REMINDER_SENT = 'INTERVIEW_REMINDER_SENT',
  ATTENDANCE_CONFIRMED = 'ATTENDANCE_CONFIRMED',
  ATTENDANCE_DECLINED = 'ATTENDANCE_DECLINED',

  // Communication
  WHATSAPP_SENT = 'WHATSAPP_SENT',
  WHATSAPP_RECEIVED = 'WHATSAPP_RECEIVED',
  TEMPLATE_SENT = 'TEMPLATE_SENT',
  FOLLOW_UP_REMINDER_SENT = 'FOLLOW_UP_REMINDER_SENT',

  // Workflow
  WORKFLOW_STARTED = 'WORKFLOW_STARTED',
  SECOND_QUIZ_COMPLETED = 'SECOND_QUIZ_COMPLETED'
}

// Main response interface - matches backend ActivityFeedDto.java
export interface ActivityFeedItem {
  id: number;
  activityType: string;           // String from backend, cast to ActivityType when needed
  quizId: number | null;
  quizTitle: string | null;
  candidateId: number | null;
  candidateName: string | null;
  actorName: string | null;
  summary: string;
  details: string | null;         // JSON string - parsed per activity type
  createdAt: string;              // ISO datetime string
}

// Period options for API calls
export type ActivityPeriod = '24h' | '7d' | '30d';

// Filter group for UI dropdown
export interface ActivityFilterGroup {
  label: string;                  // Display label (e.g., "Interview Updates")
  translationKey: string;         // i18n key (e.g., "activityFeed.interviewUpdates")
  types: ActivityType[];          // Activity types in this group
}

// Predefined filter groups for the filter dropdown
export const ACTIVITY_FILTER_GROUPS: ActivityFilterGroup[] = [
  {
    label: 'All Activities',
    translationKey: 'activityFeed.allActivities',
    types: []  // Empty = no filter, get all
  },
  {
    label: 'New Applications',
    translationKey: 'activityFeed.newApplications',
    types: [ActivityType.CANDIDATE_APPLIED]
  },
  {
    label: 'Pipeline Changes',
    translationKey: 'activityFeed.pipelineChanges',
    types: [ActivityType.CANDIDATE_MOVED]
  },
  {
    label: 'Comments',
    translationKey: 'activityFeed.comments',
    types: [ActivityType.COMMENT_ADDED]
  },
  {
    label: 'Interview Updates',
    translationKey: 'activityFeed.interviewUpdates',
    types: [
      ActivityType.INTERVIEW_BOOKED,
      ActivityType.INTERVIEW_CONFIRMED,
      ActivityType.INTERVIEW_CANCELLED,
      ActivityType.INTERVIEW_COMPLETED,
      ActivityType.INTERVIEW_NO_SHOW,
      ActivityType.INTERVIEW_REMINDER_SENT,
      ActivityType.ATTENDANCE_CONFIRMED,
      ActivityType.ATTENDANCE_DECLINED
    ]
  },
  {
    label: 'Messages',
    translationKey: 'activityFeed.messages',
    types: [
      ActivityType.WHATSAPP_SENT,
      ActivityType.WHATSAPP_RECEIVED,
      ActivityType.TEMPLATE_SENT,
      ActivityType.FOLLOW_UP_REMINDER_SENT
    ]
  },
  {
    label: 'Workflow',
    translationKey: 'activityFeed.workflow',
    types: [ActivityType.WORKFLOW_STARTED, ActivityType.SECOND_QUIZ_COMPLETED]
  }
];

// Parsed details interfaces (for type-safe JSON parsing)
export interface CandidateAppliedDetails {
  candidateName: string;
  quizTitle: string;
  score: number;
  percentage: string;
}

export interface CandidateMovedDetails {
  candidateName: string;
  fromStage: string;
  toStage: string;
}

export interface CommentAddedDetails {
  candidateName: string;
  commentText: string;
}

export interface InterviewBookedDetails {
  candidateName: string;
  slotDate: string;
  startTime: string;
  endTime: string;
  slotType: string;
  locationName: string;
}

export interface FollowUpReminderSentDetails {
  candidateName: string;
  channel: 'whatsapp' | 'email';
  nodeType: string;
  templateName?: string;
}